import { useMemo } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import dayjs, { type Dayjs } from "dayjs";
import { useTheme as useAppTheme } from "@/components/theme-provider";
import { createScheduleTimeMuiTheme } from "@/lib/mui-schedule-time-theme";

function scheduleTimeToPickerValue(startTime: string): string {
  const [h = "0", m = "0"] = (startTime || "00:00").split(":");
  return `${h.padStart(2, "0")}:${m.padStart(2, "0").slice(0, 2)}`;
}

function scheduleTimeToDayjs(startTime: string): Dayjs {
  const normalized = scheduleTimeToPickerValue(startTime);
  const [hs, ms] = normalized.split(":");
  const h = parseInt(hs || "0", 10);
  const m = parseInt(ms || "0", 10);
  return dayjs().hour(h).minute(m).second(0).millisecond(0);
}

export interface ScheduleTimePickerProps {
  value: string;
  onChange: (hhmm: string) => void;
  /** @default "Time" */
  "aria-label"?: string;
  "data-testid"?: string;
}

/**
 * MUI DesktopTimePicker (24h segments + clock) wrapped with theme matching the app.
 * Use for schedule slots, reschedule lesson, etc.
 */
export function ScheduleTimePicker({
  value,
  onChange,
  "aria-label": ariaLabel = "Time",
  "data-testid": dataTestId,
}: ScheduleTimePickerProps) {
  const { resolvedTheme } = useAppTheme();
  const muiTheme = useMemo(
    () => createScheduleTimeMuiTheme(resolvedTheme),
    [resolvedTheme]
  );

  return (
    <div className="w-full min-w-0">
      <ThemeProvider theme={muiTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopTimePicker
            ampm={false}
            ampmInClock={false}
            views={["hours", "minutes"]}
            closeOnSelect
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
            }}
            value={scheduleTimeToDayjs(value)}
            onChange={(v: Dayjs | null) => {
              if (v) onChange(v.format("HH:mm"));
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                label: null,
                inputProps: {
                  "aria-label": ariaLabel,
                  ...(dataTestId ? { "data-testid": dataTestId } : {}),
                },
              },
            openPickerButton: {
              disableRipple: true,
            },
            actionBar: {
              sx: {
                backgroundColor: "hsl(var(--popover))",
                borderTop: "1px solid hsl(var(--border))",
              },
            },
          }}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </div>
  );
}
